import { TRouteNames } from 'app/types'
import cloneDeep from 'lodash.clonedeep'
import type {
  LocationPreRegistrationConfValidationClientDto,
  OrderValidationResponseClientDto,
} from 'service-api/src/types/api'

interface TRouteData {
  route: TRouteNames
  anchor: string
}

const configRouteMapping = {
  COLOR: { route: 'profilePetEdit', anchor: '' },
  WEIGHT: { route: 'profilePetEdit', anchor: '' },
  SEX: { route: 'profilePetEdit', anchor: '' },
  ALTERED: { route: 'profilePetEdit', anchor: '' },
  DATE_OF_BIRTH: { route: 'profilePetEdit', anchor: '' },
  MEDICATION_INSTRUCTIONS: { route: 'profilePetEdit', anchor: 'instructions' },
  FEEDING_INSTRUCTIONS: { route: 'profilePetEdit', anchor: 'instructions' },
  CARE_INSTRUCTIONS: { route: 'profilePetEdit', anchor: 'instructions' },
  GROOMING_INSTRUCTIONS: { route: 'profilePetEdit', anchor: 'instructions' },
  REQUIRED_VACCINATIONS: { route: 'profilePetEdit', anchor: 'vaccines' },
  OPTIONAL_VACCINATIONS: { route: 'profilePetEdit', anchor: 'vaccines' },
  PROFILE_IMAGE: { route: 'profilePetEdit', anchor: 'photo' },
  VET_NAME: { route: 'profilePetEdit', anchor: 'vet' },
  VET_PHONE: { route: 'profilePetEdit', anchor: 'vet' },
  SAVED_CARD: { route: 'profilePayments', anchor: '' },
  EXPIRED_CARD: { route: 'profilePayments', anchor: '' },
  ASSOCIATED_CONTACT: { route: 'profileContacts', anchor: '' },
  AGREEMENT: { route: 'profileAgreements', anchor: '' },
  _DEFAULT: { route: 'myprofile', anchor: '' },
} as Record<string, TRouteData>

export const getRouteForConf = (conf: LocationPreRegistrationConfValidationClientDto) => {
  const route = configRouteMapping[conf.petAndOwnerInfo!]
  return route ?? configRouteMapping._DEFAULT
}

export const flattenConfig = (data: LocationPreRegistrationConfValidationClientDto[]) => {
  const newConf = [] as LocationPreRegistrationConfValidationClientDto[]
  let lastGroup = null
  let lastName = null
  for (let i = 0; i < data.length; i++) {
    if (data[i].bookingGroup === null) {
      newConf.push(data[i])
      lastGroup = null
      lastName = data[i].bookingDisplayName
      continue
    }
    if (data[i].bookingGroup !== lastGroup) {
      newConf.push(data[i])
      lastGroup = data[i].bookingGroup
      lastName = data[i].bookingDisplayName
      continue
    }
    if (data[i].bookingDisplayName === lastName) {
      continue
    }
    newConf[newConf.length - 1].groups ||= []
    newConf[newConf.length - 1].groups!.push(data[i])
    lastName = data[i].bookingDisplayName
  }

  return cloneDeep(newConf).sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
}

export type PreRegValidationData = ReturnType<typeof processPreRegValidation>

export const processPreRegValidation = (data: OrderValidationResponseClientDto) => {
  // sort configs by `bookingGroup` to be more easy to flatten
  data.locationUserProfile?.locationPreRegistrationConfs?.sort(
    (rc1, rc2) => rc1.bookingGroup?.localeCompare(rc2.bookingGroup || '') ?? 0
  )
  data.locationPetProfiles?.forEach((pp) =>
    pp.locationPreRegistrationConfs?.sort((rc1, rc2) => rc1.bookingGroup?.localeCompare(rc2.bookingGroup || '') ?? 0)
  )

  // flatten the configuration for pets
  data.locationPetProfiles?.forEach((pp) => {
    pp.locationPreRegistrationConfs = flattenConfig(pp.locationPreRegistrationConfs!)
  })

  // flatten the configuration for the user
  if (data.locationUserProfile) {
    data.locationUserProfile.locationPreRegistrationConfs = flattenConfig(
      data.locationUserProfile.locationPreRegistrationConfs!
    )
  }

  return data
}

export const checkCompliant = (data?: OrderValidationResponseClientDto) => {
  if (!data) {
    return false
  }
  if (!data.locationPetProfiles && !data.locationUserProfile && !data.order) {
    return true
  }
  const owner = data.locationUserProfile?.locationPreRegistrationConfs?.every((reg) => reg.compliant)
  const pet = data.locationPetProfiles?.every((pet) => pet.locationPreRegistrationConfs?.every((reg) => reg.compliant))

  return owner && pet
}

export const isVaccinationConf = (conf: LocationPreRegistrationConfValidationClientDto) => {
  if (conf.petAndOwnerInfo === 'REQUIRED_VACCINATIONS' || conf.petAndOwnerInfo === 'OPTIONAL_VACCINATIONS') {
    return true
  }
  return false
}
